import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

type RegisterLoaderActivity = () => () => void;

const LoaderActivityContext = createContext<RegisterLoaderActivity | null>(
  null
);

type LoaderActivityProviderProps = {
  children: ReactNode;
  onActiveLoaderCountChange: (count: number) => void;
};

export const LoaderActivityProvider = ({
  children,
  onActiveLoaderCountChange,
}: LoaderActivityProviderProps) => {
  const activeLoaderIds = useRef(new Set<symbol>());
  const onActiveLoaderCountChangeRef = useRef(onActiveLoaderCountChange);
  onActiveLoaderCountChangeRef.current = onActiveLoaderCountChange;

  const registerLoaderActivity = useCallback(() => {
    const loaderId = Symbol("loader-activity");
    activeLoaderIds.current.add(loaderId);
    onActiveLoaderCountChangeRef.current(activeLoaderIds.current.size);

    return () => {
      if (activeLoaderIds.current.delete(loaderId)) {
        onActiveLoaderCountChangeRef.current(activeLoaderIds.current.size);
      }
    };
  }, []);

  return (
    <LoaderActivityContext.Provider value={registerLoaderActivity}>
      {children}
    </LoaderActivityContext.Provider>
  );
};

export const useRegisterLoaderActivity = () => {
  const registerLoaderActivity = useContext(LoaderActivityContext);

  useEffect(() => registerLoaderActivity?.(), [registerLoaderActivity]);
};
